import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Download, ExternalLink, FileText, ShieldAlert, Upload } from 'lucide-react';
import SsnReviewDialog from './SsnReviewDialog';
import { downloadWithAuth, openWithAuth, isTabSafeType } from './api';
import { uploadRequestsApi } from './uploadRequestsApi';
import { formatDate } from './kit/time';
import { copyText } from './clipboard';
import { errorMessage } from './errors';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Input } from '../components/ui/input';

// Ticket 211 (211-4): the upload links staff sent a borrower and the files that
// came back. A link sent before the loan existed lives on the contact; once a
// loan file is made for the contact it moves there and says so here.
export const STATE_LABEL = {
  active: 'Waiting',
  revoked: 'Turned off',
  expired: 'Expired',
  closed: 'Loan closed',
};
const STATE_STYLE = {
  active: 'bg-emerald-50 text-emerald-700',
  revoked: 'bg-slate-100 text-slate-500',
  expired: 'bg-amber-50 text-amber-700',
  closed: 'bg-slate-100 text-slate-500',
};

const kb = (n) => (n ? (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`) : '');

export function RequestFiles({ req, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [reviewing, setReviewing] = useState(null); // doc_id of the file in the SSN editor
  if (!req.loan_visible) {
    return (
      <p className="text-[11px] text-slate-400" data-testid={`ur-hidden-${req.request_id}`}>
        {req.file_count} file{req.file_count === 1 ? '' : 's'} in a loan file you can't open.
      </p>
    );
  }
  if (!req.files.length) {
    return <p className="text-[11px] text-slate-400">Nothing uploaded yet.</p>;
  }
  // PDFs and images open in a tab; anything else is saved as a file.
  const view = async (f, save) => {
    const path = uploadRequestsApi.filePath(req.request_id, f.doc_id);
    setBusy(f.doc_id);
    try {
      if (!save && isTabSafeType(f.content_type)) await openWithAuth(path, { fallbackName: f.filename });
      else await downloadWithAuth(path, f.filename);
    } finally { setBusy(null); }
  };
  return (
    <ul className="space-y-1">
      {req.files.map((f) => (
        <li key={f.doc_id || f.filename} className="flex items-center gap-2 text-[12px]" data-testid={`ur-file-${f.doc_id}`}>
          <FileText size={12} className="shrink-0 text-slate-400" />
          <span className={`flex-1 min-w-0 truncate ${f.removed ? 'line-through text-slate-400' : ''}`} title={f.filename}>{f.filename}</span>
          <span className="text-[10px] text-slate-400 shrink-0">{kb(f.size)}</span>
          {f.needs_masking && !f.removed && (
            <>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 shrink-0"
                data-testid={`ur-held-${f.doc_id}`}><ShieldAlert size={10} /> Needs SSN masking</span>
              <button type="button" onClick={() => setReviewing(f.doc_id)}
                className="text-[10px] font-semibold px-2 py-0.5 rounded bg-brand-navy text-white shrink-0"
                data-testid={`ur-review-${f.doc_id}`}>Review</button>
              <SsnReviewDialog reviewId={f.ssn_review_id} filename={f.filename} open={reviewing === f.doc_id}
                onOpenChange={(o) => setReviewing(o ? f.doc_id : null)}
                onReviewed={() => { toast({ title: 'Audrey is re-reading the files', description: 'The upload will show here once it is read.' }); onChanged?.(); }} />
            </>
          )}
          {f.removed ? (
            <span className="text-[10px] text-slate-400 shrink-0">Removed from loan file</span>
          ) : f.doc_id && (
            <>
              <button type="button" onClick={() => view(f, false)} disabled={busy === f.doc_id}
                className="text-brand-navy hover:underline shrink-0" aria-label={`Open ${f.filename}`} data-testid={`ur-open-${f.doc_id}`}>
                <ExternalLink size={12} />
              </button>
              <button type="button" onClick={() => view(f, true)} disabled={busy === f.doc_id}
                className="text-brand-navy hover:underline shrink-0" aria-label={`Download ${f.filename}`} data-testid={`ur-download-${f.doc_id}`}>
                <Download size={12} />
              </button>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

// Turn a link off, or swap it for a new one. Neither sends anything: a new
// link is shown here for staff to copy and send themselves.
export function RequestActions({ req, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  // A link in a loan file the user cannot open is not theirs to change.
  if (!req.loan_visible) return null;

  const run = async (fn, okTitle) => {
    setBusy(true);
    try {
      const out = await fn();
      toast({ title: okTitle });
      onChanged?.();
      return out;
    } catch (e) {
      toast({ title: 'That did not work', description: errorMessage(e), variant: 'destructive' });
      return null;
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    const ok = await confirmDialog({
      title: 'Turn off this upload link?',
      description: `${req.borrower_name || 'The borrower'} can no longer upload with it. Files already sent stay.`,
      confirmText: 'Turn off',
      destructive: true,
    });
    if (!ok) return;
    // run() returns null on failure; a successful turn-off kills the link shown here.
    const out = await run(() => uploadRequestsApi.revoke(req.request_id), 'Upload link turned off');
    if (out !== null) setUrl(null);
  };

  const regenerate = async () => {
    const ok = await confirmDialog({
      title: 'Make a new upload link?',
      description: 'The old link stops working. Nothing is sent: copy the new link and send it to the borrower yourself.',
      confirmText: 'Make new link',
    });
    if (!ok) return;
    const out = await run(() => uploadRequestsApi.regenerate(req.request_id), 'New upload link ready');
    if (out) setUrl(out.upload_url);
  };

  const copy = async () => {
    toast({ title: (await copyText(url)) ? 'Link copied' : 'Copy failed, select the link and copy it' });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3 text-[11px] font-semibold">
        {req.state === 'active' && (
          <button type="button" onClick={revoke} disabled={busy} className="text-red-600 hover:underline"
            data-testid={`ur-revoke-${req.request_id}`}>Turn off</button>
        )}
        {req.state !== 'closed' && (
          <button type="button" onClick={regenerate} disabled={busy} className="text-brand-navy hover:underline"
            data-testid={`ur-regenerate-${req.request_id}`}>New link</button>
        )}
      </div>
      {url && (
        <div className="flex items-center gap-2">
          <Input readOnly value={url} onFocus={(e) => e.target.select()} aria-label="New upload link"
            className="flex-1 min-w-0 w-auto h-auto text-[11px] md:text-[11px] rounded px-2 py-1 bg-slate-50"
            data-testid={`ur-new-url-${req.request_id}`} />
          <button type="button" onClick={copy} className="text-brand-navy shrink-0" aria-label="Copy link"
            data-testid={`ur-copy-${req.request_id}`}><Copy size={12} /></button>
        </div>
      )}
    </div>
  );
}

export function RequestRow({ req, showContact = false, onChanged, children }) {
  return (
    <li className="py-3 first:pt-0 last:pb-0 space-y-1.5" data-testid={`ur-row-${req.request_id}`}>
      <div className="flex items-center gap-2">
        {showContact && req.contact_id ? (
          <Link to={`/portal/contacts/${req.contact_id}`} className="text-sm font-semibold text-brand-navy hover:underline truncate">
            {req.borrower_name}
          </Link>
        ) : (
          <span className="text-[12px] text-slate-600 truncate">
            Sent {formatDate(req.created_at)}{req.requested_by_name ? ` by ${req.requested_by_name}` : ''}
          </span>
        )}
        <span className={`ml-auto shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATE_STYLE[req.state] || STATE_STYLE.revoked}`}
          data-testid={`ur-state-${req.request_id}`}>
          {STATE_LABEL[req.state] || req.state}
        </span>
      </div>
      {showContact && (
        <div className="text-[11px] text-slate-500">
          Sent {formatDate(req.created_at)}{req.requested_by_name ? ` by ${req.requested_by_name}` : ''}
        </div>
      )}
      {req.doc_types_needed.length > 0 && (
        <div className="text-[11px] text-slate-500">Asked for: {req.doc_types_needed.join(', ')}</div>
      )}
      {req.state === 'active' && req.expires_at && (
        <div className="text-[11px] text-slate-400">Link works until {formatDate(req.expires_at)}</div>
      )}
      {req.loan_file_id && req.loan_visible && (
        <Link to={`/portal/loans/${req.loan_file_id}`} className="text-[11px] font-semibold text-brand-navy hover:underline"
          data-testid={`ur-loan-${req.request_id}`}>
          In loan file {req.loan_number || ''}
        </Link>
      )}
      <RequestFiles req={req} onChanged={onChanged} />
      {children}
    </li>
  );
}

// Contact page sidebar. Hidden when the contact was never sent a link.
export default function UploadRequestsCard({ contactId, canIssue = false }) {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    uploadRequestsApi.list({ contact_id: contactId })
      .then(setItems)
      .catch(() => setItems([]));
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="upload-requests-card">
      <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-500">
        <Upload size={14} /> Borrower uploads
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((r) => (
          <RequestRow key={r.request_id} req={r} onChanged={load}>
            {canIssue && <RequestActions req={r} onChanged={load} />}
          </RequestRow>
        ))}
      </ul>
    </div>
  );
}
